// packages/shared-ui/utils/debugTrace.ts
//
// 永続デバッグトレース（共通部品 / プロダクト非依存）
// =====================================================
// リダイレクト・リロード・AWS 認証フロー（Cognito signOut / refresh 等）の途中で
// console ログが消えてしまい「何が起きたか」が追えない挙動を、localStorage に
// 永続記録して後から解析するための汎用ユーティリティ。
//
// 設計方針:
// - どのプロダクト(unizo/portal/admin/dev-lab)でも import するだけで使える。
// - 既定では完全に無効（no-op）。顧客端末・本番(prod)・staging には何も出さない。
//   各プロダクトが起動時に `configureDebugTrace(ENV.ENV === "dev")` を呼ぶことで
//   dev 環境でだけ自動有効化する。
// - 任意の環境で一時的に調べたい開発者向けに、当該ブラウザのみの手動 opt-in を用意:
//   DevTools で `localStorage.__debugTrace = "1"` をセット（リセットは削除）。
//   既定オフなので顧客には影響しない。
//
// 使い方（例: signOut の発火源を追う）:
//   import { configureDebugTrace, trace, dumpTrace } from "@shared-ui/utils/debugTrace";
//   configureDebugTrace(ENV.ENV === "dev");           // 起動時に1回
//   trace("auth-signout", "challenge-cancel", { global: true }, { stack: true });
//   // 後で解析（発火した端末の DevTools で）:
//   dumpTrace("auth-signout");                          // console.table で一覧

const MANUAL_OPT_IN_KEY = "__debugTrace";
const STORAGE_PREFIX = "debugTrace:";
const MAX_ENTRIES_PER_CHANNEL = 50;

// プロダクトが configureDebugTrace で設定する有効フラグ（既定オフ）
let configuredEnabled = false;

/**
 * トレース1件の形。`data` は呼び出し側の任意フィールドが展開される。
 */
export interface DebugTraceEntry {
  /** ISO8601 タイムスタンプ */
  t: string;
  /** 論理チャンネル（例: "auth-signout"） */
  channel: string;
  /** イベント名 / 理由（例: "challenge-cancel"） */
  event: string;
  /** 記録時の URL（origin+pathname のみ。search/hash は機微情報保護のため除外）*/
  url: string;
  /** opts.stack=true のときのみ付与されるコールスタック */
  stack?: string;
  /** 呼び出し側が渡した任意データ */
  [key: string]: unknown;
}

/** localStorage が利用可能か（SSR / 制限環境ガード） */
function hasLocalStorage(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

/**
 * プロダクト起動時に呼び、dev 環境でのみ自動有効化する。
 * 例: `configureDebugTrace(ENV.ENV === "dev")`
 */
export function configureDebugTrace(on: boolean): void {
  configuredEnabled = on;
  // dev 等で有効化された時点で Console ヘルパーを生やす（無効化時は何もしない）。
  if (on) installDebugTraceConsole();
}

/**
 * 実効的に有効か。
 * - プロダクトが dev で有効化した（configuredEnabled）か、
 * - 当該ブラウザで手動 opt-in（localStorage.__debugTrace === "1"）した場合に true。
 * いずれも無ければ false（＝顧客/本番では何も記録しない）。
 */
export function isDebugTraceEnabled(): boolean {
  if (configuredEnabled) return true;
  if (!hasLocalStorage()) return false;
  try {
    return window.localStorage.getItem(MANUAL_OPT_IN_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * トレースを1件記録する。無効時は完全な no-op。
 * @param channel 論理チャンネル（localStorage キーは `debugTrace:<channel>`）
 * @param event   イベント名 / 理由
 * @param data    任意の付随データ（エントリに展開される）
 * @param opts.stack true でコールスタックを記録（発火源の特定に有用）
 */
export function trace(
  channel: string,
  event: string,
  data: Record<string, unknown> = {},
  opts: { stack?: boolean } = {},
): void {
  if (!isDebugTraceEnabled() || !hasLocalStorage()) return;
  try {
    // 固定フィールドは data の後に展開し、caller の data による上書きを防ぐ。
    // url は機微情報（?email=/?code=/#authCode= 等）の永続化を避けるため
    // origin+pathname のみ記録し、search/hash は意図的に落とす（RULES Rule5）。
    const entry: DebugTraceEntry = {
      ...data,
      t: new Date().toISOString(),
      channel,
      event,
      url:
        typeof location !== "undefined"
          ? location.origin + location.pathname
          : "",
    };
    if (opts.stack) {
      entry.stack = new Error().stack;
    }
    const key = STORAGE_PREFIX + channel;
    const existing = readTrace(channel);
    existing.push(entry);
    window.localStorage.setItem(
      key,
      JSON.stringify(existing.slice(-MAX_ENTRIES_PER_CHANNEL)),
    );
    // 直後にリダイレクトしても残るが、生きている間は console にも出す
    console.debug(`[debugTrace:${channel}]`, event, entry);
  } catch {
    // トレースの失敗で本体機能を絶対に壊さない
  }
}

/** 指定チャンネルのトレースを配列で取得する（無ければ空配列）。 */
export function readTrace(channel: string): DebugTraceEntry[] {
  if (!hasLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + channel);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DebugTraceEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * トレースを console.table で見やすく出力する（解析用）。
 * channel 省略時は `debugTrace:*` の全チャンネルをまとめて出力。
 */
export function dumpTrace(channel?: string): void {
  if (!hasLocalStorage()) return;
  try {
    if (channel) {
      console.table(readTrace(channel));
      return;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        console.group(key);
        console.table(readTrace(key.slice(STORAGE_PREFIX.length)));
        console.groupEnd();
      }
    }
  } catch {
    // no-op
  }
}

/** トレースを消去する。channel 省略時は全チャンネルを削除。 */
export function clearTrace(channel?: string): void {
  if (!hasLocalStorage()) return;
  try {
    if (channel) {
      window.localStorage.removeItem(STORAGE_PREFIX + channel);
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // no-op
  }
}

/** 記録済みチャンネル名の一覧を返す（`localStorage` の `debugTrace:<channel>` を走査）。 */
export function listTraceChannels(): string[] {
  if (!hasLocalStorage()) return [];
  try {
    const out: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        out.push(key.slice(STORAGE_PREFIX.length));
      }
    }
    return out;
  } catch {
    return [];
  }
}

// DevTools の Console から直接呼べるよう window に生やすグローバル名（有効時のみ）。
// チャンネル名を覚えていなくても `traceChannels()` で一覧でき、`dumpTrace()` で全件出せる。
const CONSOLE_GLOBALS = {
  traceChannels: listTraceChannels,
  dumpTrace,
  readTrace,
  clearTrace,
} as const;

/**
 * 有効時のみ、DevTools Console から呼べるヘルパーを `window` に生やす。
 * 無効（顧客/本番で未 opt-in）なら何もしない。多重呼び出しは冪等。
 */
export function installDebugTraceConsole(): void {
  if (!isDebugTraceEnabled() || typeof window === "undefined") return;
  try {
    const w = window as unknown as Record<string, unknown>;
    if (w.__debugTraceConsoleInstalled) return;
    Object.assign(w, CONSOLE_GLOBALS);
    w.__debugTraceConsoleInstalled = true;
    console.info(
      "[debugTrace] Console ヘルパー有効: traceChannels() / dumpTrace(channel?) / readTrace(channel) / clearTrace(channel?)",
    );
  } catch {
    // no-op
  }
}

// 手動 opt-in（localStorage.__debugTrace="1" 設定後リロード）でも使えるよう、
// モジュールロード時にも有効なら生やす。dev では configureDebugTrace 側でも生やす。
installDebugTraceConsole();
